import { useEffect, type FC, type ReactNode, useRef, useState, useMemo, ComponentProps } from 'react';
import { useLocation } from 'react-router';
import { Form, Radio } from 'antd';
import { Button, InputText, Select, Space, Text } from '@g2u/ui-kit';
import { SearchOutlined } from '@ant-design/icons';
import { DefaultOptionType } from 'antd/es/select';
import { format, startOfToday, subDays } from 'date-fns';

import { JobList } from 'components';
import type { Technician, SystemUser } from 'types';
import { jobFilters, type JobFilters } from 'queries';
import { useTechnicianSearch } from 'helpers/hooks/useTechnicianSearch';
import { addSpacesToNumber, useSearchParamsInUrl } from 'helpers';
import { useJobSearch } from 'helpers/hooks/useJobSearch';

import styles from './search-job.module.scss';

type SearchParams = Required<NonNullable<Parameters<typeof useJobSearch>[0]>>;
type SearchByValues = NonNullable<SearchParams['searchBy']>;
type SearchByOption = { label: ReactNode; value: SearchByValues; disabled?: boolean };

const searchByOptions: SearchByOption[] = [
  {
    label: <>Job ID</>,
    value: 'job_id',
  },
  {
    label: <>Technician</>,
    value: 'technician_id',
  },
  {
    label: <>Customer ID</>,
    value: 'customer_id',
  },
  {
    label: <>Created By</>,
    value: 'created_by',
  },
];

type FormFields = {
  searchBy: SearchByValues;
  searchedValue: string;
  filter: JobFilters | JobFilters[];
  bookedTime: 'all' | 'last7' | 'more7';
};

export const SearchJob: FC = () => {
  const [form] = Form.useForm<FormFields>();

  const { state } = useLocation();

  const [{ searchBy, searchValue, page, recordsPerPage }, setSearchParams] = useSearchParamsInUrl<SearchParams>({
    searchBy: 'job_id',
    searchValue: state?.searchValue || '',
  });

  const formSearchBy = Form.useWatch('searchBy', { form });
  const formFilter = Form.useWatch('filter', { form });
  const [bookingRange, setBookingRange] = useState<any>({
    createdFrom: undefined,
    createdTo: undefined,
  });
  const [selectSearchValue, setSelectSearchValue] = useState<string>(''); // to manually control Select open state and hide list before user types 1st char
  const searchByNumber = formSearchBy && formSearchBy !== 'all';
  const prevSearchByNumber = useRef<boolean>(undefined);
  const { technicians, isLoading: isTechsLoading } = useTechnicianSearch({
    searchParam: 'byName',
    searchValue: '',
    page: 1,
    perPage: 2000,
  });

  useEffect(() => {
    if (searchByNumber === true && prevSearchByNumber.current === false) form.setFieldValue('searchedValue', '');
    prevSearchByNumber.current = searchByNumber;
  }, [searchByNumber]);

  useEffect(() => {
    const isQuickSearch = !!state?.searchValue || state?.searchValue === '';
    isQuickSearch &&
      setSearchParams({
        searchBy: 'job_id',
        searchValue: state?.searchValue,
      });

    if (state?.searchValue) form.setFieldValue('searchedValue', state.searchValue);
  }, [state]);

  const handleFormSubmit = (fields: FormFields) => {
    const formSearchBy = fields['searchBy'];
    const formSearchedValue = fields['searchedValue'];
    const formBookedTime = fields['bookedTime'];
    const date = formBookedTime !== 'all' ? format(subDays(startOfToday(), 7), 'yyyy-MM-dd') : undefined;
    setBookingRange({
      createdFrom: formBookedTime === 'last7' ? date : undefined,
      createdTo: formBookedTime === 'more7' ? date : undefined,
    });

    // Sync form state to url
    setSearchParams({
      searchBy: formSearchBy,
      searchValue: formSearchedValue || '',
    });
  };

  const [jobs, total = 0, isLoading, jobCreators] = useJobSearch({
    searchBy,
    searchValue,
    page,
    recordsPerPage,
    filter: formFilter,
    createdFrom: bookingRange.createdFrom,
    createdTo: bookingRange.createdTo,
  });

  const toOptions = (data?: Technician[] | SystemUser[]) =>
    data?.map((item) => ({ label: item.name, value: item.ID.toString() })) || [];
  const techOptions = useMemo(() => toOptions(technicians), [technicians]);
  const creatorOptions = useMemo(() => toOptions(jobCreators), [jobCreators]);
  const optionsMap: Record<string, DefaultOptionType[]> = {
    technician_id: techOptions,
    created_by: creatorOptions,
  };

  const paginationMetadata: ComponentProps<typeof JobList>['paginationMetadata'] = {
    total,
    current_page: page,
    onChange(page, pageSize) {
      setSearchParams({
        page,
        recordsPerPage: pageSize,
      });
    },
    pageSize: recordsPerPage,
  };

  useEffect(() => {
    // update main form after "quick" search
    searchBy && form.setFieldValue('searchBy', searchBy);
  }, [searchBy]);

  return (
    <div className={styles.searchJob}>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} fullWidth>
        <Form form={form} layout="vertical" className={styles.searchJob__form} onFinish={handleFormSubmit}>
          <Space.$ compact>
            <Select.$
              name="searchBy"
              rules={[{}]}
              dataTestId="search-by-select"
              className={styles.searchJob__formSelect}
              initialValue={searchBy}
              suffixIcon={undefined}
              options={searchByOptions}
              onChange={() => {
                form.setFieldValue('searchedValue', undefined);
              }}
            />
            {formFilter === 'open' && (
              <div className={styles.searchJob__formSelect}>
                <Text.$ size={Text.Size.MD} className={styles.searchJob__formSelectLabel}>
                  Booked Time:
                </Text.$>
                <Select.$
                  name="bookedTime"
                  rules={[{}]}
                  dataTestId="search-by-select"
                  initialValue={'all'}
                  suffixIcon={undefined}
                  options={[
                    { label: 'All', value: 'all' },
                    { label: '1-7 days ago', value: 'last7' },
                    { label: '> 7 days ago', value: 'more7' },
                  ]}
                />
              </div>
            )}

            {['technician_id', 'created_by'].includes(formSearchBy) ? (
              <Select.$
                name="searchedValue"
                onSearch={(val) => setSelectSearchValue(val)}
                rules={[{}]}
                showSearch
                dataTestId="earched-value-select"
                suffixIcon={undefined}
                onSelect={() => {
                  setSelectSearchValue('');
                }}
                onBlur={() => {
                  setSelectSearchValue('');
                }}
                onClear={() => {
                  setSelectSearchValue('');
                  form.setFieldValue('searchedValue', '');
                }}
                open={selectSearchValue?.length > 0}
                options={optionsMap[formSearchBy]}
                allowClear
                className={styles.searchJob__formSearch}
                loading={isTechsLoading}
              />
            ) : (
              <InputText.$
                fullWidth
                name="searchedValue"
                placeholder=""
                dataTestId="searched-value-input"
                noMargin
                className={styles.searchJob__formSearch}
                maxLength={searchByNumber ? 7 : undefined}
                formatter={searchByNumber ? (val) => addSpacesToNumber(val?.toString(), []) : undefined}
              />
            )}
            <Form.Item>
              <Button.$
                type={Button.Type.Primary}
                dataTestId="search-submit-btn"
                onClick={() => null}
                htmlType="submit"
                icon={<SearchOutlined />}
              />
            </Form.Item>
          </Space.$>
          <Form.Item name="filter" initialValue={'all'} className={styles.searchJob__formButtonGroup}>
            <Radio.Group
              options={jobFilters.map((option) => ({
                label: option,
                value: option,
              }))}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
        </Form>
      </Space.$>

      <JobList isLoading={isLoading} jobs={jobs} paginationMetadata={paginationMetadata} />
    </div>
  );
};
